import { openDB } from 'idb';

// structure for sending updates to the remote alkebulan service
export interface ResourceDelta {
  username: string;
  oil_barrels?: number;
  gold?: number;
  cobalt?: number;
  uranium?: number;
  // sending countries array is interpreted as *new* countries acquired or sold.
  // the backend will append or remove accordingly based on context; for purchases
  // just include the one(s) bought.
  countries?: string[];
}

export async function updateResources(delta: ResourceDelta): Promise<void> {
  try {
    const db = await openDB('alkebulan-game', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('gameState')) {
          db.createObjectStore('gameState', { keyPath: 'username' });
        }
      }
    });

    const current = (await db.get('gameState', delta.username)) as GameState | undefined;
    const state: GameState = current || { username: delta.username, answers: [] };

    if (typeof delta.gold === 'number') {
      state.gold = (state.gold || 0) + delta.gold;
    }
    if (typeof delta.cobalt === 'number') {
      state.cobalt = (state.cobalt || 0) + delta.cobalt;
    }
    if (typeof delta.uranium === 'number') {
      state.uranium = (state.uranium || 0) + delta.uranium;
    }
    if (typeof delta.oil_barrels === 'number') {
      state.oil = (state.oil || 0) + delta.oil_barrels;
    }
    if (Array.isArray(delta.countries)) {
      const nextCountries = new Set(state.countries || []);
      delta.countries.forEach((c) => nextCountries.add(c));
      state.countries = Array.from(nextCountries);
    }

    await db.put('gameState', state);
  } catch (err) {
    console.warn('Failed to update local resource state in IndexedDB:', err);
  }
}

export async function fetchResources(username: string): Promise<{
  oil_barrels: number;
  gold: number;
  cobalt: number;
  uranium: number;
  countries: string[];
} | null> {
  try {
    const db = await openDB('alkebulan-game', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('gameState')) {
          db.createObjectStore('gameState', { keyPath: 'username' });
        }
      }
    });
    const state = (await db.get('gameState', username)) as GameState | undefined;
    if (!state) return null;

    return {
      oil_barrels: state.oil || 0,
      gold: state.gold || 0,
      cobalt: state.cobalt || 0,
      uranium: state.uranium || 0,
      countries: state.countries || []
    };
  } catch (err) {
    console.warn('Unable to retrieve resources from IndexedDB:', err);
    return null;
  }
}

export async function saveGameState(state: GameState): Promise<void> {
  try {
    const db = await openDB('alkebulan-game', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('gameState')) {
          db.createObjectStore('gameState', { keyPath: 'username' });
        }
      }
    });
    // store the entire state, including any resource fields
    await db.put('gameState', state);

  } catch (err) {
    console.error('Error saving game state to IndexedDB:', err);
  }
}
// Feedback and streak logic for answer selection
export type FeedbackType = 'correct' | 'wrong' | 'streak' | 'bravo' | 'boss';

export interface FeedbackResult {
  feedback: FeedbackType;
  flipGif: string;
  reward?: 'gold' | 'cobalt' | 'uranium';
  rewardAmount?: number;
}
export function getFeedback(streakMemory: boolean[], correct: boolean): FeedbackResult {
  // streakMemory: array of last N answers (true/false)
  // correct: whether current answer is correct
  let feedback: FeedbackType = correct ? 'correct' : 'wrong';
  let flipGif = correct ? '/alkebulan/correct.gif' : '/alkebulan/wrong.gif';
  let reward: 'gold' | 'cobalt' | 'uranium' | undefined;
  let rewardAmount: number | undefined;

  if (!correct) {
    // Streak broken
    return { feedback: 'wrong', flipGif: '/alkebulan/wrong.gif' };
  }

  // Check streaks
  const streak = streakMemory.filter(x => x).length === streakMemory.length ? streakMemory.length : 0;
  if (streak === 3) {
    feedback = 'streak';
    flipGif = '/alkebulan/streak.gif';
    reward = 'gold';
    rewardAmount = 1;
  } else if (streak === 4) {
    feedback = 'bravo';
    flipGif = '/alkebulan/bravo.gif';
    reward = 'cobalt';
    rewardAmount = 1;
  } else if (streak === 5) {
    feedback = 'boss';
    flipGif = '/alkebulan/boss.gif';
    reward = 'uranium';
    rewardAmount = 1;
  }
  return { feedback, flipGif, reward, rewardAmount };
}

export interface GameQuestion {
  question: string;
  options: string[];
  answer: number; // index of correct answer
  explanation?: string;
}

export type GameAnswer = { id: string | number };

export interface GameState {
  username: string;
  answers?: GameAnswer[];
  rewardAmount?: number;
  // optional fields for tracking lives and lock state
  lives?: number;
  lockedUntil?: number; // timestamp (ms) until which the player is barred from playing
  // inventory / resources earned by playing
  gold?: number;
  cobalt?: number;
  uranium?: number;
  oil?: number;
  // list of countries the user has purchased in the Freedom marketplace
  countries?: string[];
}

export async function loadGameState(username: string): Promise<GameState | null> {
  try {
    const db = await openDB('alkebulan-game', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('gameState')) {
          db.createObjectStore('gameState', { keyPath: 'username' });
        }
      }
    });
    const state: GameState | null = (await db.get('gameState', username)) || null;
    return state;
  } catch (err) {
    console.error('Error loading game state from IndexedDB:', err);
    return null;
  }
}

